import { useState, ReactElement, useRef, useEffect, useMemo, useCallback } from "react";

import RFLayout from "@/layout/RFLayout";
import MessageBlock from "@/containers/copilot/MessageBlock";
import TextareaAutosize from "react-textarea-autosize";
import { useChats, pollChatBlocks, sendMessage, createNewChat } from "@/hooks/useRequest";
import { Block } from "@/interfaces/copilotTypes";
import { nanoid } from "nanoid";
import Toast from "@/components/Toast";
import EmptyChat from "@/containers/copilot/EmptyChat";
import useEventListener from "@/hooks/useEventListener";
import { useProfile } from "@/service/common.services";
import CopilotContainer from "@/containers/copilot/CopilotContainer";
import SafeArea from "@/components/safe-area";
import log2Sentry from "@/utils/log2Sentry";
import { CopilotError } from "@/utils/sentryError";
import { GetServerSideProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import rfStore from "@/utils/rfStore";
import { useTranslation } from "next-i18next";
import rfLocalStore from "@/utils/rfLocalStore";
import HeaderView from "@/containers/copilot/HeaderView";
import SubmitBtn from "@/containers/copilot/SubmitBtm";
import { Bots } from "@/interfaces/enums";
import getFullWebCdnUrl from "@/utils/getFullWebCdnUrl";

enum ResponseMsgStatus {
	pending,
	cancel,
}

export default function Copilot({ locale }: { locale: string }) {
	// 设置惟一id
	rfLocalStore.setRockBotUUID();

	// 设置请求语言参数
	const rfState = rfStore.getRFState();
	if (rfState) {
		rfStore.setRFState({
			...rfState,
			language: locale.toLocaleLowerCase() === "zh-hans" ? "zh-cn" : locale,
		});
	}
	const { t } = useTranslation("copilot");

	const botOptions = [
		{
			label: "RockBot",
			value: Bots.rockBot,
			disabled: false,
			tag: "Hot",
			icon: getFullWebCdnUrl("/copilot/rock-bot.png"),
		},
		{
			label: "TeslaBot",
			value: Bots.teslaBot,
			disabled: false,
			tag: "Beta",
			icon: getFullWebCdnUrl("/copilot/tesla-bot.png"),
		},
		// {
		//   label: "MuskBot",
		//   value: Bots.muskBot,
		//   disabled: true,
		//   tag: "Coming Soon",
		//   icon: "https://web-cdn.rockflow.tech/h5/earning/rock-bot.png",
		// },
	];

	const [bot, setBot] = useState(Bots.rockBot);

	const [messages, setMessages] = useState<Block[]>([]);
	const [loading, setLoading] = useState(false);
	const [input, setInput] = useState("");
	const [lastMessage, setLastMessage] = useState<Block | null>(null);
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const footerRef = useRef<HTMLDivElement>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	const prevScrollY = useRef(0);
	const enableAutoScroll = useRef(true);
	const switchChatIdStatusRef = useRef<Map<string, ResponseMsgStatus>>(new Map());

	const { chat_id, mutate, isLoading: chatLoading } = useChats(bot);
	const { data: profile } = useProfile();

	const botName = useMemo(() => {
		return botOptions.find((_bot) => bot === _bot.value)?.label;
	}, [bot]);

	useEffect(() => {
		if (chat_id) {
			let loadingStatus = false;
			console.log(chat_id, "chat_id");
			switchChatIdStatusRef.current?.set(chat_id, ResponseMsgStatus.pending);

			const initHistory = async () => {
				setLoading(true);
				try {
					const response = await pollChatBlocks(chat_id);
					const blocks = Array.isArray(response.blocks) ? response.blocks : [];
					setMessages(response.blocks);
					if (blocks.length > 0) {
						const lastMessage = blocks[blocks.length - 1];
						console.log("lastMsg", lastMessage);
						loadingStatus = lastMessage?.status == "pending";
						if (loadingStatus) {
							console.log(switchChatIdStatusRef.current);
							subscribe(chat_id);
						}
					}
				} catch (error: any) {
					Toast.fail(`Get History Err: ${error.message}`);
					log2Sentry(new CopilotError(error.message), "error", error);
				} finally {
					setLoading(loadingStatus);
				}
			};

			initHistory();
		}
	}, [chat_id]);

	useEffect(() => {
		if (scrollRef.current && enableAutoScroll.current) {
			scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
		}
	}, [messages.length, lastMessage]);

	const handleBotChange = (_bot: Bots) => {
		clearBotMsg();
		setBot(_bot);
	};
	// 取消操作
	const clearBotMsg = () => {
		if (chat_id) {
			switchChatIdStatusRef.current?.set(chat_id, ResponseMsgStatus.cancel);
			setMessages([]);
			setLoading(false);
		}
	};

	useEventListener(
		"scroll",
		(event) => {
			const currScrollY = scrollRef.current?.scrollTop || 0;

			if (prevScrollY.current - currScrollY > 10) {
				// 用户手动回滚，暂时取消自动scroll
				enableAutoScroll.current = false;
			}

			prevScrollY.current = currScrollY || 0;
		},
		scrollRef,
	);
	// 处理IOS键盘遮挡
	useEventListener(
		"focusin",
		() => {
			const isAppIOS = rfState?.model && rfState?.model !== "android";

			if (!isAppIOS) return;

			// if (scrollRef.current) {
			//   scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
			// }
			setTimeout(() => {
				// 等系统动画完
				footerRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
			}, 200);
		},
		inputRef,
	);
	// 处理Android键盘遮挡
	useEventListener("resize", () => {
		const isAndroid = rfState?.model === "android";
		if (rfState?.model && !isAndroid) return;
		setTimeout(() => {
			// 等系统动画完
			// if (scrollRef.current) {
			//   scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
			// }
			footerRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
		}, 200);
	});

	const subscribe = async (_chat_id: string) => {
		try {
			if (!_chat_id) {
				return;
			}

			// 取消情况
			console.log(switchChatIdStatusRef.current?.get(_chat_id));
			if (switchChatIdStatusRef.current?.get(_chat_id) === ResponseMsgStatus.cancel) {
				return;
			}

			const response = await pollChatBlocks(_chat_id);
			const blocks = response.blocks;

			const lastMessage = blocks[blocks.length - 1];

			setMessages(blocks);
			setLastMessage(lastMessage);

			if (lastMessage.status == "final") {
				setLoading(false);
				return;
			}

			if (!lastMessage.content) {
				await new Promise((resolve) => setTimeout(resolve, 600));
			}
			await subscribe(_chat_id);
		} catch (error: any) {
			// 处理错误
			Toast.fail(`Subscribe Msg Err: ${error.message}`);
			// 发生错误后，等待一段时间再进行下一次订阅，延迟 1 秒
			setTimeout(() => subscribe(_chat_id), 1000);
			log2Sentry(new CopilotError(error.message), "error", error);
		}
	};

	const handleSubmit = async () => {
		if (loading) return;
		if (!input) {
			return Toast.fail("Message cannot be empty");
		}
		if (!chat_id) {
			return Toast.fail("Chat ID does not exist");
		}

		enableAutoScroll.current = true;
		setLoading(true);
		// 本地乐观更新
		setMessages((prev) => [
			...prev,
			{
				content: input,
				status: "pending",
				role: "user",
				_id: nanoid(),
				object: "block",
			},
		]);
		try {
			const message = input;
			setInput("");
			inputRef.current?.blur();
			await sendMessage({ chat_id, message });
			await subscribe(chat_id);
		} catch (error: any) {
			Toast.fail(error.message);
			log2Sentry(new CopilotError(error.message), "error", error);
		} finally {
			setLoading(false);
		}
	};

	const handleCreate = async () => {
		if (chatLoading) {
			return;
		}

		clearBotMsg();
		setLoading(true);
		try {
			await createNewChat(bot);
			await mutate();
		} catch (error: any) {
			Toast.fail(`Create Chat Err: ${error.message}`);
			log2Sentry(new CopilotError(error.message), "error", error);
		} finally {
			setLoading(false);
		}
	};

	const getAvatar = useCallback(
		(role: string) => {
			if (role === "user") {
				return profile?.avatar ? profile.avatar : "https://avatar-cdn.rockflow.tech/avatar-default/avatar-default.png";
			}
			return botOptions.find((_bot) => _bot.value === bot)?.icon;
		},
		[profile?.avatar, bot],
	);

	const getNickname = useCallback(
		(role: string) => {
			if (role === "user") {
				return profile?.nickname ? profile.nickname : "Rocker";
			}
			return botOptions.find((_bot) => _bot.value === bot)?.label;
		},
		[bot, profile],
	);

	const showEmptyUI = useMemo(() => {
		return !loading && !chatLoading && messages.length == 0;
	}, [loading, chatLoading, messages.length]);

	const submitBtnDisabled = useMemo(() => {
		if (!input || loading || chatLoading) return true;
		return false;
	}, [chatLoading, input, loading]);

	const handleExampleClick = (text: string) => {
		setInput(text);
	};

	return (
		<CopilotContainer>
			<div className="mx-auto flex h-full max-w-3xl flex-col overflow-y-hidden font-singolare">
				{/* header */}

				<HeaderView
					onCreate={handleCreate}
					bot={bot}
					botName={botName || ""}
					botOptions={botOptions}
					onBotChange={handleBotChange}
					loading={loading}
					locale={locale}
				/>
				{/* content */}
				<div className="w-ful flex-1 overflow-y-auto overflow-x-hidden" ref={scrollRef}>
					{showEmptyUI && (
						<div className="flex h-full w-full flex-col items-center justify-end">
							<EmptyChat bot={bot} botName={botName || ""} onExampleClick={handleExampleClick} />
						</div>
					)}

					{messages.map((m, i) => (
						<MessageBlock key={m._id} block={m} avatar={getAvatar(m.role)} nickname={getNickname(m.role)} />
					))}
				</div>

				{/* footer */}
				<div className="flex-shrink-0 bg-white">
					<div className="flex w-full items-end py-6" ref={footerRef}>
						<div className=" relative ml-4 flex flex-1 rounded-3xl border border-[#9B9EAA]">
							<TextareaAutosize
								className="m-0 w-full resize-none rounded-3xl border-0 bg-transparent px-4 py-2.5"
								ref={inputRef}
								minRows={1}
								maxRows={4}
								placeholder={t("ask-rockbot-a-question", { bot: botName }) + "..."}
								value={input}
								onChange={(e) => setInput(e.target.value)}
								onKeyDown={(e) => {
									if (e.key == "Enter" && !e.shiftKey) {
										e.preventDefault();
										handleSubmit();
									}
								}}
								autoFocus
							/>
						</div>

						<SubmitBtn loading={loading || chatLoading} disabled={submitBtnDisabled} onSubmit={handleSubmit} />
					</div>

					<SafeArea position="bottom" />
				</div>

				{/* 结束 */}
			</div>
		</CopilotContainer>
	);
}

Copilot.getLayout = function (page: ReactElement) {
	return <RFLayout>{page}</RFLayout>;
};

export const getServerSideProps: GetServerSideProps = async ({ locale = "en" }) => {
	return {
		props: {
			...(await serverSideTranslations(locale, ["copilot", "error", "common", "login"])),
			locale,
		},
	};
};
